import { grey } from "@mui/material/colors";

import { format } from "date-fns";

import type { SelectSaleConfigurationValues } from "components/word-processor-field/dialogs/word-processor-select-sale-configuration-dialog.component";
import { PRODUCT_SUB_TYPES } from "constants/products";
import {
  FEE_PRODUCT_TYPE,
  PRODUCT_TYPES,
  SERVICE_PRODUCT_TYPE
} from "constants/sales";
import { getEarText } from "features/inventory/orders/orders.util";
import type { Product } from "services/types/products.type";
import { type LineItem, type Sale } from "services/types/sales.type";
import {
  formatDateOnlyValue,
  formatMoney,
  formatMoneyValue,
  formatPercent
} from "utils/common";
import {
  getLineItemEarDisplay,
  getLineItemSerialRaw
} from "utils/line-item-display.util";

import {
  getConfigurableLineItemValue,
  getConfigurableLineItems,
  getConfigurableLineItemsHeaders,
  getConfigurableTotals
} from "./configurable-merge-tags";
import { resolveTableFontSizePx } from "./table-font-size.utils";
import {
  excludeReturnedOrExchanged,
  getSaleTotals,
  getSortedItems
} from "./utils";

export const getSalesItemsTableHtml = (sale: Sale) => {
  let result = `<table style="border-collapse: collapse; width: 100%;" border="1">
  <colgroup>
    <col style="width: 11.1111%;">
    <col style="width: 11.1111%;">
    <col style="width: 11.1111%;">
    <col style="width: 11.1111%;">
    <col style="width: 11.1111%;">
    <col style="width: 11.1111%;">
    <col style="width: 11.1111%;">
    <col style="width: 11.1111%;">
    <col style="width: 11.1111%;">
  </colgroup>
  <tbody>
    <tr>
      <td><strong>Description</strong></td>
      <td><strong>Type</strong></td>
      <td><strong>Unit Price</strong></td>
      <td><strong>Tax</strong></td>
      <td><strong>Discount</strong></td>
      <td><strong>Quantity</strong></td>
      <td><strong>Line Total</strong></td>
      <td><strong>Patient Responsibility</strong></td>
      <td><strong>Insurance Responsibility</strong></td>
    </tr>`;

  sale?.items?.forEach((saleLineItem) => {
    const serialNumbers = getLineItemSerialRaw(saleLineItem);
    const ears = getLineItemEarDisplay(saleLineItem);

    let row = `<td>${saleLineItem.description ?? ""}${
      serialNumbers ? " - S/N: " + serialNumbers : ""
    }}${ears ? " - Ear: " + ears : ""} ${
      saleLineItem.returned_at
        ? "<br>" +
          format(new Date(String(saleLineItem.returned_at)), "MM/dd/yyyy")
        : ""
    }
      </td>`;

    row =
      row +
      `<td>${PRODUCT_TYPES.getFromCode(
        saleLineItem.product.master_product.type
      )}</td>`;

    row =
      row +
      `<td style="text-align: right">${formatMoney(
        Number(saleLineItem.price),
        2
      )}</td>`;
    row =
      row +
      `<td style="text-align: right">${
        saleLineItem.taxable && saleLineItem.tax_rate
          ? formatPercent(Number(saleLineItem.tax_rate), 0, 3)
          : "--"
      }</td>`;

    row =
      row +
      `<td style="text-align: right">${formatMoney(
        Number(saleLineItem.discount),
        2
      )}<br><span style="color:${grey[600]}">${
        saleLineItem?.discount && Number(saleLineItem.discount) > 0
          ? String(
              Math.round(
                (Number(saleLineItem.discount) / Number(saleLineItem.price)) *
                  100 *
                  100
              ) / 100
            )
          : "0"
      }%</span></td>`;

    row = row + `<td style="text-align: right">${saleLineItem.quantity}</td>`;
    row =
      row +
      `<td style="text-align: right">${formatMoney(
        Number(saleLineItem.total),
        2
      )}</td>`;

    row =
      row +
      `<td style="text-align: right">${formatMoney(
        Number(saleLineItem.patient_responsibility),
        2
      )}</td>`;

    row =
      row +
      `<td style="text-align: right">${formatMoney(
        Number(saleLineItem.insurer_responsibility),
        2
      )}</td>`;

    result = result + "<tr>" + row + "</tr>";
  });

  result =
    result +
    `<tr style="border: none">
      <td colspan="9"><br></td>
    </tr>`;

  result =
    result +
    `<tr style="border: none">
      <td colspan="7"><br></td>
      <td>Patient Responsibility</td>
      <td style="text-align: right">${formatMoney(
        Number(sale.patient_responsibility_total)
      )}</td>
    </tr>`;

  result =
    result +
    `<tr style="border: none">
      <td colspan="7"><br></td>
      <td>Insurance Responsibility</td>
      <td style="text-align: right">${formatMoney(
        Number(sale.insurer_responsibility_total)
      )}</td>
    </tr>`;

  result =
    result +
    `<tr style="border: none">
      <td colspan="7"><br></td>
      <td style="font-weight: 700">Sale Total</td>
      <td style="font-weight: 700;text-align: right">${formatMoney(
        Number(sale.total)
      )}</td>
    </tr>`;

  result = result + `</tbody></table>`;
  return result;
};

export const getLineItemsTableHtml = (items: LineItem[] | undefined) => {
  let result = `<table style="border-collapse: collapse; width: 100%;" border="1">
  <colgroup>
    <col style="width: 14.2857%;">
    <col style="width: 14.2857%;">
    <col style="width: 14.2857%;">
    <col style="width: 14.2857%;">
    <col style="width: 14.2857%;">
    <col style="width: 14.2857%;">
    <col style="width: 14.2857%;">
  </colgroup>
  <tbody>
    <tr>
      <td style="text-align: center"><strong>Ear</strong></td>
      <td style="text-align: center"><strong>Manufacturer</strong></td>
      <td style="text-align: center"><strong>Model</strong></td>
      <td style="text-align: center"><strong>Serial #</strong></td>
      <td style="text-align: center"><strong>MFR Warranty Exp</strong></td>
      <td style="text-align: center"><strong>L&D Exp</strong></td>
      <td style="text-align: center; width: 120px"><strong>Price</strong></td>
    </tr>`;

  items?.forEach((item) => {
    const ear = getEarText(item.inventory_product?.ear);
    const manufacturer = item.product?.master_product?.manufacturer?.name ?? "";
    const model = item.product?.master_product?.model ?? "";
    const serialNumber = item.inventory_product?.serial_number ?? "";

    const manufacturer_warranty_expiration_date =
      item.inventory_product?.manufacturer_warranty_expiration_date ?? "";

    const loss_damage_warranty_expiration_date =
      item.inventory_product?.loss_damage_warranty_expiration_date ?? "";

    const price = formatMoneyValue(Number(item.price || "0"));

    result = `${result}
    <tr>
      <td>${ear}</td>
      <td>${manufacturer}</td>
      <td>${model}</td>
      <td>${serialNumber}</td>
      <td style="text-align: right">${
        manufacturer_warranty_expiration_date
          ? format(
              new Date(manufacturer_warranty_expiration_date),
              "MM/dd/yyyy"
            )
          : ""
      }</td>
      <td style="text-align: right">${
        loss_damage_warranty_expiration_date
          ? format(new Date(loss_damage_warranty_expiration_date), "MM/dd/yyyy")
          : ""
      }</td>
      <td style="text-align: right; width: 120px">${price}</td>
    </tr>`;
  });

  return `${result}</tbody></table>`;
};

const getProductTypeText = (product: Product) => {
  const type = product?.master_product?.type || product.type;
  const subtype = product?.master_product?.subtype;

  if (!type) return "";
  if (type && subtype) {
    return `${PRODUCT_TYPES.getFromCode(
      type
    )} (${PRODUCT_SUB_TYPES.getFromCode(subtype)})`;
  }

  return PRODUCT_TYPES.getFromCode(type);
};

export const getSaleDeviceLineItems = (items: LineItem[]) => {
  return excludeReturnedOrExchanged(items).filter((item) => {
    const type = item.product?.master_product?.type || item.product?.type;
    return type !== SERVICE_PRODUCT_TYPE && type !== FEE_PRODUCT_TYPE;
  });
};

export const getSaleServiceLineItems = (items: LineItem[]) => {
  return excludeReturnedOrExchanged(items).filter((item) => {
    const type = item.product?.master_product?.type || item.product?.type;
    return type === SERVICE_PRODUCT_TYPE || type === FEE_PRODUCT_TYPE;
  });
};

const getTotalsHtmlRow = (sale: Sale, showResponsibility?: boolean) => {
  const {
    paymentMethods,
    totalDiscounts,
    totalTax,
    totalServices,
    total,
    balance
  } = getSaleTotals(sale);

  return `
  <tr>
    <td colspan="${showResponsibility ? 10 : 9}" style="text-align: right;">Services/Dispensing&nbsp;Fees</td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(totalServices)}</td>
  </tr>
  <tr>
    <td colspan="${showResponsibility ? 10 : 9}" style="text-align: right;">Total Discounts</td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(totalDiscounts)}</td>
  </tr>
  <tr>
    <td colspan="${showResponsibility ? 10 : 9}" style="text-align: right">Total Tax</td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(totalTax)}</td>
  </tr>
  <tr>
    <td colspan="${showResponsibility ? 10 : 9}" style="text-align: right"><strong>Total Amount</strong></td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(total)}</td>
  </tr>
  <tr>
    <td colspan="${showResponsibility ? 10 : 9}" style="text-align: left">Payment Method${paymentMethods?.length > 1 ? "s:" : ":"} ${paymentMethods.join(", ")}</td>
    <td style="text-align: right; width: 120px"></td>
  </tr>
  <tr>
    <td colspan="${showResponsibility ? 10 : 9}" style="text-align: right"><strong>Balance Due</strong></td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(balance, balance < 0)}</td>
  </tr>
  `;
};

const SALE_LINE_ITEMS_TABLE_COLGROUP = `<colgroup>
    <col style="width: 10%;">
    <col style="width: 10%;">
    <col style="width: 10%;">
    <col style="width: 10%;">
    <col style="width: 10%;">
    <col style="width: 10%;">
    <col style="width: 10%;">
    <col style="width: 10%;">
    <col style="width: 10%;">
    <col style="width: 10%;">
  </colgroup>`;

export const getSaleLineItemsTotalsTableHtml = (sale: Sale | undefined) => {
  if (!sale) return undefined;

  const items = sale?.items || [];
  if (!items.length) return "";

  let result = `<table style="border-collapse: collapse; width: 100%;" border="1">
   ${SALE_LINE_ITEMS_TABLE_COLGROUP}
  <tbody>
    `;

  return `${result}${getTotalsHtmlRow(sale)}</tbody></table>`;
};

export const getSaleLineItemsServicesTableHtml = (
  sale: Sale | undefined,
  hidePrice?: boolean
) => {
  if (!sale) return undefined;
  const filteredItems = getSortedItems(
    getSaleServiceLineItems(sale?.items || [])
  );
  if (!filteredItems.length) return "";

  const hasDiscount = hidePrice
    ? false
    : filteredItems.some((item) => Number(item.discount) > 0);

  let result = `<table style="border-collapse: collapse; width: 100%;" border="1">
   ${SALE_LINE_ITEMS_TABLE_COLGROUP}
  <tbody>
    <tr>
      <td style="text-align: center"><strong>CPT Code</strong></td>
      <td colspan="${hidePrice ? 3 : 2}" style="text-align: center"><strong>Product</strong></td>
      <td colspan="${hasDiscount ? 4 : 5}" style="text-align: center"><strong>Description</strong></td>
      ${hasDiscount ? '<td style="text-align: center"><strong>Discount</strong></td>' : ""}
      ${hidePrice ? "" : '<td style="text-align: center; width: 120px"><strong>Price</strong></td>'}
      <td style="text-align: center"><strong>Quantity</strong></td>
    </tr>
      `;

  filteredItems.forEach((item) => {
    const cptCode = item.cpt_code?.code ?? "";
    const product = getProductTypeText(item.product);

    const quantity = Number(item.quantity || 1);
    const price = formatMoneyValue(Number(item.price || "0") * quantity);

    const description = item.description;
    const discount = Number(item.discount)
      ? formatMoneyValue(Number(item.discount))
      : "";

    result = `${result}
    <tr>
      <td>${cptCode}</td>
      <td colspan="${hidePrice ? 3 : 2}">${product}</td>
      <td colspan="${hasDiscount ? 4 : 5}">${description}</td>
      ${hasDiscount ? '<td style="text-align: right">' + discount + "</td>" : ""}
      ${hidePrice ? "" : '<td style="text-align: right; width: 120px">' + price + "</td>"}
      <td style="text-align: right">${quantity}</td>
      </tr>
      `;
  });

  return `${result}</tbody></table>`;
};

export const getTotalsWithPredefinedParametersHtmlRow = (params: {
  sale: Sale;
  total_discounts: boolean;
  total_tax: boolean;
  total_amount: boolean;
  total_patient_payments: boolean;
  total_insurance_payments: boolean;
  amount_due_from_insurance: boolean;
  amount_due_from_patient: boolean;
  balance_due: boolean;
  show_responsibility: boolean;
  services_dispensing_fees: boolean;
}) => {
  const {
    sale,
    total_discounts,
    total_tax,
    total_amount,
    total_patient_payments,
    total_insurance_payments,
    amount_due_from_insurance,
    amount_due_from_patient,
    balance_due,
    show_responsibility,
    services_dispensing_fees
  } = params;
  const {
    paymentMethods,
    totalDiscounts,
    totalTax,
    totalServices,
    total,
    balance,
    totalInsurancePayments,
    totalPatientPayments,
    totalPatientSurcharge,
    amountDueFromInsurance,
    amountDueFromPatient
  } = getSaleTotals(sale);

  let result = "";

  let colspan = show_responsibility ? 10 : 9;

  if (services_dispensing_fees) {
    result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: right;">Services/Dispensing&nbsp;Fees</td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(totalServices)}</td>
  </tr>`;
  }
  if (total_discounts) {
    result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: right;">Total Discounts</td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(totalDiscounts)}</td>
  </tr>`;
  }

  if (total_tax) {
    result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: right">Total Tax</td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(totalTax)}</td>
  </tr>`;
  }

  if (total_amount) {
    result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: right"><strong>Total Amount</strong></td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(total)}</td>
  </tr>`;
  }

  if (total_patient_payments) {
    result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: right">Total Patient Payments</td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(totalPatientPayments)}</td>
  </tr>`;

    if (totalPatientSurcharge > 0) {
      result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: right">Total Surcharge</td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(totalPatientSurcharge)}</td>
  </tr><tr>
    <td colspan="${colspan}" style="text-align: right">Total Charged</td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(totalPatientPayments + totalPatientSurcharge)}</td>
  </tr>`;
    }

    if (paymentMethods.length) {
      result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: left">Payment Method${paymentMethods?.length > 1 ? "s:" : ":"} ${paymentMethods.join(", ")}</td>
    <td style="text-align: right; width: 120px"></td>
  </tr>`;
    }
  }

  if (total_insurance_payments) {
    result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: right">Total Insurance Payments</td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(totalInsurancePayments)}</td>
  </tr>`;
  }

  if (amount_due_from_insurance) {
    result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: right">Amount Due From Insurance</td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(amountDueFromInsurance)}</td>
  </tr>`;
  }

  if (amount_due_from_patient) {
    result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: right">Amount Due From Patient</td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(amountDueFromPatient)}</td>
  </tr>`;
  }

  if (balance_due) {
    result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: right"><strong>Balance Due</strong></td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(balance, balance < 0)}</td>
  </tr>`;
  }

  return result;
};

// Relative width of each configurable column, weighted by the content it
// typically holds (a date needs more room than a quantity). Normalized to
// percentages and emitted as a <colgroup>, which all three render paths
// honor: the browser/editor natively, Enhanced Table Rendering via
// deriveColumnPercentsFromColgroup, and the plain react-pdf-html path via
// the per-cell width styles below.
const COLUMN_WIDTH_WEIGHTS: Record<string, number> = {
  Ear: 5,
  "CPT Code": 5,
  Product: 7,
  Description: 10,
  Manufacturer: 8,
  Model: 8,
  // ENG-2477: a Catalog name is the longest text in the row ("Starkey BTE Digital Evolv AI 1000 LiION").
  Name: 12,
  Color: 5,
  Battery: 5,
  Material: 6,
  "Serial #": 7,
  "MFR Warranty Exp": 7,
  "L&D Warranty Exp": 7,
  "Service Plan Exp": 7,
  "L&D Deductible": 6,
  Price: 10,
  Qty: 4,
  "Resp.": 13,
  "Canal Length": 5,
  "Canal Venting": 5,
  Gain: 5,
  Length: 4,
  "Dome Type": 5,
  "Dome Size": 5
};
const DEFAULT_COLUMN_WIDTH_WEIGHT = 6;

// A compact column holds a fixed-shape value (a money amount, a quantity, an
// ear side) that cannot use extra room. Normalizing the weights to 100% hands
// these columns the surplus width of a lean table — an agreement whose empty
// columns dropped down to 7 rendered a ~150px Price column and a ~200px
// totals value cell. Cap their share of the table; the descriptive text
// columns absorb the surplus instead. On a letter page the money caps land
// near the 120px the legacy fixed tables always used.
const COLUMN_MAX_PERCENTS: Record<string, number> = {
  Ear: 9,
  Qty: 8,
  Price: 14,
  "Resp.": 15
};

const applyColumnMaxPercents = (
  headers: string[],
  percents: number[]
): number[] => {
  const caps = headers.map(
    (header) => COLUMN_MAX_PERCENTS[header] ?? Number.POSITIVE_INFINITY
  );
  const result = [...percents];

  // Water-fill: clamp columns over their cap and hand the excess to columns
  // still below theirs, proportional to current share. A receiver can hit its
  // own cap in turn, so iterate; every pass caps at least one more column.
  for (let pass = 0; pass < headers.length; pass++) {
    let excess = 0;
    result.forEach((percent, index) => {
      if (percent > caps[index]) {
        excess += percent - caps[index];
        result[index] = caps[index];
      }
    });
    if (excess < 0.005) break;

    const receiverTotal = result.reduce(
      (sum, percent, index) => (percent < caps[index] ? sum + percent : sum),
      0
    );
    if (!receiverTotal) {
      // Every column is at its cap — the colgroup must still fill the table,
      // so scale the capped shape back up to 100%.
      const total = result.reduce((a, b) => a + b, 0) || 1;
      return result.map((percent) => (percent / total) * 100);
    }

    const factor = excess / receiverTotal;
    result.forEach((percent, index) => {
      if (percent < caps[index]) {
        result[index] = percent + percent * factor;
      }
    });
  }

  return result;
};

const getColumnPercents = (headers: string[]): number[] => {
  const weights = headers.map(
    (header) => COLUMN_WIDTH_WEIGHTS[header] ?? DEFAULT_COLUMN_WIDTH_WEIGHT
  );
  const total = weights.reduce((a, b) => a + b, 0) || 1;
  const percents = weights.map((w) => (w / total) * 100);
  return applyColumnMaxPercents(headers, percents).map(
    (percent) => Math.round(percent * 100) / 100
  );
};

const buildColgroup = (percents: number[]) =>
  percents.length
    ? `<colgroup>${percents.map((p) => `<col style="width: ${p}%;">`).join("")}</colgroup>`
    : "";

// A column the user enabled but that has no value on any line item carries
// no information — it only steals width from the columns that do. Rendered
// with every data point enabled, dropping the empty ones is the difference
// between a readable chart and confetti.
const hasVisibleContent = (value: string | undefined | null) =>
  Boolean(
    String(value ?? "")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/gi, " ")
      .trim().length
  );

// The totals value must hold a money amount without wrapping; a single
// trailing column of a dense table is too narrow. Span trailing columns
// until the value cell has at least this share of the table.
const TOTALS_VALUE_MIN_PERCENT = 11;

const getTotalsValueColspan = (percents: number[]): number => {
  let span = 0;
  let width = 0;
  for (let i = percents.length - 1; i >= 0; i--) {
    span += 1;
    width += percents[i];
    if (width >= TOTALS_VALUE_MIN_PERCENT) break;
  }
  return Math.max(span, 1);
};

const buildTableHeader = (
  headers: string[],
  percents: number[],
  fontSize: string
) => {
  let headerHtml = `<table style="border-collapse: collapse; width: 100%;" border="1">${buildColgroup(percents)}<thead><tr>`;

  headers.forEach((header, index) => {
    const displayText = header === "Serial #" ? "Serial&nbsp;#" : header;

    headerHtml += `<td style="font-size:${fontSize};box-sizing:border-box;text-align:center;width:${percents[index]}%;"><span style="font-size:${fontSize};"><strong>${displayText}</strong></span></td>`;
  });

  headerHtml += `</tr></thead><tbody>`;
  return headerHtml;
};

const buildTableCell = (
  header: string,
  value: string,
  percent: number,
  fontSize: string
) => {
  const isRightAligned =
    header === "Price" || header === "Qty" || header === "Resp.";

  const alignment = isRightAligned ? "right" : "left";

  return `<td style="font-size:${fontSize};box-sizing:border-box;text-align:${alignment};width:${percent}%;"><span style="font-size:${fontSize};">${value}</span></td>`;
};

const buildTableRows = (
  valueRows: string[][],
  headers: string[],
  percents: number[],
  fontSize: string
) => {
  let rowsHtml = "";

  valueRows.forEach((values) => {
    let row = "";

    headers.forEach((header, index) => {
      row += buildTableCell(header, values[index], percents[index], fontSize);
    });

    if (row) {
      rowsHtml += `<tr>${row}</tr>`;
    }
  });

  return rowsHtml;
};

export const getSaleLineItemsTableHtmlWithPredefinedParameters = (
  sale: Sale | undefined,
  saleConfiguration: Partial<SelectSaleConfigurationValues>
) => {
  if (!sale || !saleConfiguration) return undefined;

  const items = getConfigurableLineItems(sale, saleConfiguration);
  if (!items.length && !saleConfiguration.include_totals) return "";

  let headers = getConfigurableLineItemsHeaders(saleConfiguration);
  if (!headers.length && !saleConfiguration.include_totals) return "";

  const configuredColumnCount = headers.length;

  // Drop columns that are empty on every line item (only when there are
  // items — a totals-only table keeps its configured header row).
  if (items.length) {
    const keptHeaders = headers.filter((header) =>
      items.some((item) =>
        hasVisibleContent(
          getConfigurableLineItemValue(item, header, saleConfiguration)
        )
      )
    );

    // Every configured column was empty on every item: without totals there
    // is nothing to show — return empty rather than a degenerate table stub.
    if (!keptHeaders.length && !saleConfiguration.include_totals) return "";
    headers = keptHeaders;
  }

  // The size follows the columns that print, not the ones configured: a dropped column takes no width (ENG-2457).
  // A size picked in the Define Parameters dialog, which caps by the configured count, never exceeds this cap.
  const fontSizeColumnCount =
    items.length && headers.length ? headers.length : configuredColumnCount;
  const fontSize = `${resolveTableFontSizePx(saleConfiguration, fontSizeColumnCount)}px`;

  const valueRows = items.map((item) =>
    headers.map((header) =>
      getConfigurableLineItemValue(
        item,
        header,
        saleConfiguration,
        fontSizeColumnCount
      )
    )
  );

  const percents = getColumnPercents(headers);

  let result = buildTableHeader(headers, percents, fontSize);
  result += buildTableRows(valueRows, headers, percents, fontSize);

  if (saleConfiguration.include_totals) {
    if (headers.length) {
      result += `<tr><td colspan="${headers.length}">&nbsp;</td></tr>`;
    }
    result += getConfigurableTotals(
      sale,
      saleConfiguration,
      headers.length,
      getTotalsValueColspan(percents),
      fontSizeColumnCount
    );
  }

  result += `</tbody></table>`;

  return result;
};

export const getSaleLineItemsTableHtml = (
  sale: Sale | undefined,
  filterFn: (items: LineItem[]) => LineItem[],
  includeTotals?: boolean,
  hidePrice?: boolean,
  showResponsibility?: boolean
) => {
  if (!sale) return undefined;
  const items = getSortedItems(filterFn(sale?.items || []));
  if (!items.length) return "";

  let result = `<table style="border-collapse: collapse; width: 100%;" border="1">
  ${SALE_LINE_ITEMS_TABLE_COLGROUP}
  <tbody>
    <tr>
      <td style="text-align: center"><strong>Ear</strong></td>
      <td ${hidePrice ? "colspan='2'" : ""} style="text-align: center"><strong>Product</strong></td>
      <td style="text-align: center;width: 120px"><strong>Manufacturer</strong></td>
      <td style="text-align: center"><strong>Model</strong></td>
      <td style="text-align: center"><strong>Color</strong></td>
      <td style="text-align: center"><strong>Serial&nbsp;#</strong></td>
      <td style="text-align: center"><strong>MFR Warranty Exp</strong></td>
      <td style="text-align: center${hidePrice ? ";width: 120px" : ""}"><strong>L&D Warranty Exp</strong></td>
      ${hidePrice ? "" : '<td style="text-align: center; width: 120px"><strong>Price</strong></td>'}
      <td style="text-align: center"><strong>Quantity</strong></td>
      ${!hidePrice && showResponsibility ? "<td style='text-align: center; width: 120px'><strong>Resp.</strong></td>" : ""}
    </tr>
      `;

  items.forEach((item) => {
    const inventoryProduct = item.inventory_product;
    const masterProduct = item.product?.master_product;

    const ear = getLineItemEarDisplay(item);
    const color = inventoryProduct?.color ?? "";

    const product = getProductTypeText(item.product);

    const manufacturer = masterProduct?.manufacturer?.name ?? "";

    const model = masterProduct?.model ?? "";
    const serialNumber = getLineItemSerialRaw(item);

    const manufacturerWarrantyExpirationDate =
      inventoryProduct?.manufacturer_warranty_expiration_date ?? "";

    const lossDamageWarrantyExpirationDate =
      inventoryProduct?.loss_damage_warranty_expiration_date ?? "";

    const quantity = Number(item.quantity || 1);
    const price = formatMoneyValue(Number(item.price || "0") * quantity);

    const resp =
      showResponsibility && !hidePrice
        ? `PT: ${formatMoneyValue(Number(item.patient_responsibility || "0"))}<br>${item.managed_care_plan?.id ? "MCP" : "INS"}: ${formatMoneyValue(Number(item.insurer_responsibility || "0"))}`
        : "";

    result = `${result}
    <tr>
      <td>${ear}</td>
      <td ${hidePrice ? "colspan='2'" : ""}>${product}</td>
      <td>${manufacturer}</td>
      <td>${model}</td>
      <td>${color}</td>
      <td>${serialNumber}</td>
      <td style="text-align: right">${
        manufacturerWarrantyExpirationDate
          ? formatDateOnlyValue(
              manufacturerWarrantyExpirationDate,
              "MMM dd, yyyy"
            )
          : ""
      }</td>
      <td style="text-align: right${hidePrice ? ";width: 120px" : ""}">${
        lossDamageWarrantyExpirationDate
          ? formatDateOnlyValue(
              lossDamageWarrantyExpirationDate,
              "MMM dd, yyyy"
            )
          : ""
      }</td>
      ${hidePrice ? "" : '<td style="text-align: right; width: 120px">' + price + "</td>"}
      <td style="text-align: right">${quantity}</td>
      ${!hidePrice && showResponsibility ? "<td style='text-align: right;width: 120px'>" + resp + "</td>" : ""}
      </tr>
      `;
  });

  if (includeTotals) {
    const colspan = hidePrice ? 9 : showResponsibility ? 11 : 10;

    result = `${result}<tr><td colspan="${colspan}">&nbsp;</td></tr>${getTotalsHtmlRow(sale, showResponsibility)}`;
  }

  return `${result}</tbody></table>`;
};
